import React, { useState, useEffect } from 'react';
import RefreshIcon from '@mui/icons-material/Refresh';
import SearchIcon from '@mui/icons-material/Search';
import CloseIcon from '@mui/icons-material/Close';
import VisibilityIcon from '@mui/icons-material/Visibility';
import EditIcon from '@mui/icons-material/Edit';
import AddIcon from '@mui/icons-material/Add';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import { useApi } from '../../hooks/useApi';
import Loader from '../common/Loader';
import OrgDetails from './OrgDetails';
import './Organizations.css';

const formatDate = (val) => {
  if (val == null) return '—';
  if (val?.toDate) return val.toDate().toLocaleString();
  const d = new Date(val);
  return isNaN(d.getTime()) ? String(val) : d.toLocaleString();
};

const PAGE_SIZES = [20, 50, 100];

const getPageNumbers = (current, total) => {
  if (total <= 7) return Array.from({ length: total }, (_, i) => i + 1);
  const pages = new Set([1, total]);
  for (let i = Math.max(1, current - 2); i <= Math.min(total, current + 2); i++) {
    pages.add(i);
  }
  const sorted = [...pages].sort((a, b) => a - b);
  const result = [];
  for (let i = 0; i < sorted.length; i++) {
    if (i > 0 && sorted[i] - sorted[i - 1] > 1) result.push(`gap-${i}`);
    result.push(sorted[i]);
  }
  return result;
};

const PLAN_BADGE = {
  free: 'badge-gray',
  pro: 'badge-blue',
  enterprise: 'badge-orange',
};

const Organizations = () => {
  const { apiCall } = useApi();

  // View routing: list vs detail
  const [selectedOrgId, setSelectedOrgId] = useState(null);

  // List state
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [orgs, setOrgs] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [sortField, setSortField] = useState('created_at');
  const [sortDir, setSortDir] = useState('desc');
  const [pageSize, setPageSize] = useState(20);
  const [currentPage, setCurrentPage] = useState(1);
  const [total, setTotal] = useState(0);
  const [totalPages, setTotalPages] = useState(1);

  // Create modal
  const [showCreate, setShowCreate] = useState(false);
  const [createForm, setCreateForm] = useState({ name: '', plan: 'free', storage_quota_mb: 1024 });
  const [createLoading, setCreateLoading] = useState(false);

  // Edit modal
  const [editOrg, setEditOrg] = useState(null);
  const [editForm, setEditForm] = useState({ name: '', plan: '', storage_quota_mb: 0 });
  const [editLoading, setEditLoading] = useState(false);

  // Delete modal
  const [deleteOrg, setDeleteOrg] = useState(null);
  const [deleteInput, setDeleteInput] = useState('');
  const [deleteLoading, setDeleteLoading] = useState(false);

  // 204-safe wrapper: useApi's response.json() throws on empty body
  const apiCallNoBody = async (endpoint, options) => {
    try {
      return await apiCall(endpoint, options);
    } catch (err) {
      if (err instanceof SyntaxError) return null;
      throw err;
    }
  };

  const fetchOrgs = async (page = currentPage, size = pageSize, q = debouncedSearch) => {
    setLoading(true);
    setError('');
    try {
      let url = `/admin/organizations?page=${page}&page_size=${size}`;
      if (q && q.trim()) url += `&q=${encodeURIComponent(q.trim())}`;
      const data = await apiCall(url);
      setOrgs(data.organizations || []);
      setTotal(data.total || 0);
      setTotalPages(data.total_pages || 1);
    } catch (err) {
      setError(err.message);
    }
    setLoading(false);
  };

  // Debounce search input
  useEffect(() => {
    const t = setTimeout(() => { setDebouncedSearch(searchTerm); setCurrentPage(1); }, 300);
    return () => clearTimeout(t);
  }, [searchTerm]);

  useEffect(() => {
    fetchOrgs(currentPage, pageSize, debouncedSearch);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentPage, pageSize, debouncedSearch]);

  const handleSort = (field) => {
    if (sortField === field) {
      setSortDir((d) => (d === 'asc' ? 'desc' : 'asc'));
    } else {
      setSortField(field);
      setSortDir('asc');
    }
  };

  const SortIndicator = ({ field }) => {
    if (sortField !== field) return <span className="sort-indicator inactive">↕</span>;
    return <span className="sort-indicator">{sortDir === 'asc' ? '↑' : '↓'}</span>;
  };

  const handleCreate = async () => {
    setCreateLoading(true);
    setError('');
    try {
      await apiCall('/organizations', {
        method: 'POST',
        body: JSON.stringify(createForm),
      });
      setShowCreate(false);
      setCreateForm({ name: '', plan: 'free', storage_quota_mb: 1024 });
      await fetchOrgs();
    } catch (err) {
      setError(err.message);
    }
    setCreateLoading(false);
  };

  const openEdit = (org) => {
    setEditOrg(org);
    setEditForm({ name: org.name || '', plan: org.plan || 'free', storage_quota_mb: org.storage_quota_mb || 0 });
  };

  const handleEdit = async () => {
    setEditLoading(true);
    setError('');
    try {
      const payload = {};
      if (editForm.name !== editOrg.name) payload.name = editForm.name;
      if (editForm.plan !== editOrg.plan) payload.plan = editForm.plan;
      if (editForm.storage_quota_mb !== editOrg.storage_quota_mb) payload.storage_quota_mb = editForm.storage_quota_mb;
      if (Object.keys(payload).length > 0) {
        await apiCall(`/admin/organizations/${editOrg.id}`, {
          method: 'PUT',
          body: JSON.stringify(payload),
        });
      }
      setEditOrg(null);
      await fetchOrgs();
    } catch (err) {
      setError(err.message);
    }
    setEditLoading(false);
  };

  const handleDelete = async () => {
    setDeleteLoading(true);
    setError('');
    try {
      await apiCallNoBody(`/admin/organizations/${deleteOrg.id}`, { method: 'DELETE' });
      setDeleteOrg(null);
      setDeleteInput('');
      await fetchOrgs();
    } catch (err) {
      setError(err.message);
    }
    setDeleteLoading(false);
  };

  // Client-side sort on current page (search is now server-side via ?q=)
  const displayed = [...orgs].sort((a, b) => {
    let aVal = a[sortField];
    let bVal = b[sortField];
    if (aVal == null) return 1;
    if (bVal == null) return -1;
    if (aVal < bVal) return sortDir === 'asc' ? -1 : 1;
    if (aVal > bVal) return sortDir === 'asc' ? 1 : -1;
    return 0;
  });

  // Detail view
  if (selectedOrgId) {
    return <OrgDetails orgId={selectedOrgId} onBack={() => setSelectedOrgId(null)} />;
  }

  // List view
  if (loading) return <Loader />;

  return (
    <div className="orgs-page">
      <div className="page-header">
        <h2 className="page-title">Organizations ({total})</h2>
        <div className="header-actions">
          <button onClick={() => setShowCreate(true)} className="btn btn-primary">
            <AddIcon sx={{ fontSize: '1rem' }} /> Create
          </button>
          <button onClick={fetchOrgs} className="btn-icon" disabled={loading}>
            <RefreshIcon className={loading ? 'spinning' : ''} sx={{ fontSize: '1.25rem' }} />
          </button>
        </div>
      </div>

      {error && <div className="error-message">{error}</div>}

      <div className="search-bar">
        <div className="search-input-wrapper">
          <SearchIcon className="search-icon" sx={{ fontSize: '1.25rem' }} />
          <input
            type="text"
            placeholder="Search by organization name..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="search-input"
          />
          {searchTerm && (
            <button onClick={() => setSearchTerm('')} className="clear-search-btn">
              <CloseIcon sx={{ fontSize: '1rem' }} />
            </button>
          )}
        </div>
      </div>

      <div className="table-card">
        <div className="users-table-wrapper">
          <table className="users-table">
            <thead>
              <tr>
                <th className="th-sortable" onClick={() => handleSort('name')}>
                  Name <SortIndicator field="name" />
                </th>
                <th className="th-sortable" onClick={() => handleSort('plan')}>
                  Plan <SortIndicator field="plan" />
                </th>
                <th className="th-sortable" onClick={() => handleSort('storage_quota_mb')}>
                  Storage (MB) <SortIndicator field="storage_quota_mb" />
                </th>
                <th className="th-sortable" onClick={() => handleSort('created_at')}>
                  Created <SortIndicator field="created_at" />
                </th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {displayed.length === 0 ? (
                <tr>
                  <td colSpan={5} className="no-results">
                    {debouncedSearch.trim() ? `No results for "${debouncedSearch.trim()}"` : 'No organizations found'}
                  </td>
                </tr>
              ) : (
                displayed.map((org) => (
                  <tr key={org.id} className="user-row">
                    <td>{org.name || '—'}</td>
                    <td>
                      <span className={`badge ${PLAN_BADGE[org.plan] || 'badge-gray'}`}>
                        {org.plan || '—'}
                      </span>
                    </td>
                    <td className="td-storage">{org.storage_quota_mb != null ? org.storage_quota_mb.toLocaleString() : '—'}</td>
                    <td className="td-date">{formatDate(org.created_at)}</td>
                    <td className="td-actions">
                      <button className="btn-icon-sm" title="View details" onClick={() => setSelectedOrgId(org.id)}>
                        <VisibilityIcon sx={{ fontSize: '1.125rem' }} />
                      </button>
                      <button className="btn-icon-sm btn-shield" title="Edit organization" onClick={() => openEdit(org)}>
                        <EditIcon sx={{ fontSize: '1.125rem' }} />
                      </button>
                      <button className="btn-icon-sm btn-delete" title="Delete organization" onClick={() => setDeleteOrg(org)}>
                        <DeleteOutlineIcon sx={{ fontSize: '1.125rem' }} />
                      </button>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        <div className="table-footer">
          <div className="rows-per-page">
            <span>Rows per page:</span>
            <select value={pageSize} onChange={(e) => { setPageSize(Number(e.target.value)); setCurrentPage(1); }}>
              {PAGE_SIZES.map((s) => <option key={s} value={s}>{s}</option>)}
            </select>
          </div>
          <span className="pagination-info">
            {total === 0
              ? 'No results'
              : `${(currentPage - 1) * pageSize + 1}–${Math.min(currentPage * pageSize, total)} of ${total}`}
          </span>
          <div className="pagination">
            <button
              className="page-btn page-nav"
              onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}
              disabled={currentPage === 1}
              title="Previous page"
            >
              <ChevronLeftIcon sx={{ fontSize: '1.25rem' }} />
            </button>
            {getPageNumbers(currentPage, totalPages).map((p) =>
              typeof p === 'string' ? (
                <span key={p} className="page-ellipsis">…</span>
              ) : (
                <button
                  key={p}
                  className={`page-btn${p === currentPage ? ' active' : ''}`}
                  onClick={() => setCurrentPage(p)}
                >
                  {p}
                </button>
              )
            )}
            <button
              className="page-btn page-nav"
              onClick={() => setCurrentPage((p) => Math.min(totalPages, p + 1))}
              disabled={currentPage === totalPages}
              title="Next page"
            >
              <ChevronRightIcon sx={{ fontSize: '1.25rem' }} />
            </button>
          </div>
        </div>
      </div>

      {/* Create Organization Modal */}
      {showCreate && (
        <div className="modal-overlay" onClick={() => !createLoading && setShowCreate(false)}>
          <div className="modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3 className="modal-title">Create Organization</h3>
              <button onClick={() => setShowCreate(false)} className="btn-close" disabled={createLoading}>
                <CloseIcon sx={{ fontSize: '1.25rem' }} />
              </button>
            </div>
            <div className="modal-body">
              <div className="form-group">
                <label className="form-label">Name</label>
                <input
                  type="text"
                  value={createForm.name}
                  onChange={(e) => setCreateForm({ ...createForm, name: e.target.value })}
                  className="form-input"
                  placeholder="Organization name"
                />
              </div>
              <div className="form-group">
                <label className="form-label">Plan</label>
                <select
                  value={createForm.plan}
                  onChange={(e) => setCreateForm({ ...createForm, plan: e.target.value })}
                  className="detail-select"
                  style={{ width: '100%' }}
                >
                  <option value="free">Free</option>
                  <option value="pro">Pro</option>
                  <option value="enterprise">Enterprise</option>
                </select>
              </div>
              <div className="form-group">
                <label className="form-label">Storage Quota (MB)</label>
                <input
                  type="number"
                  min="0"
                  value={createForm.storage_quota_mb}
                  onChange={(e) => setCreateForm({ ...createForm, storage_quota_mb: parseInt(e.target.value) || 0 })}
                  className="form-input"
                />
              </div>
            </div>
            <div className="modal-footer modal-footer-right">
              <button className="btn btn-secondary" onClick={() => setShowCreate(false)} disabled={createLoading}>
                Cancel
              </button>
              <button
                className="btn btn-primary"
                onClick={handleCreate}
                disabled={createLoading || !createForm.name.trim()}
              >
                {createLoading ? 'Creating...' : 'Create'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Edit Organization Modal */}
      {editOrg && (
        <div className="modal-overlay" onClick={() => !editLoading && setEditOrg(null)}>
          <div className="modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3 className="modal-title">Edit Organization</h3>
              <button onClick={() => setEditOrg(null)} className="btn-close" disabled={editLoading}>
                <CloseIcon sx={{ fontSize: '1.25rem' }} />
              </button>
            </div>
            <div className="modal-body">
              <div className="detail-row" style={{ marginBottom: '0.75rem' }}>
                <span className="detail-label">ID:</span>
                <span className="detail-value detail-value-sm" style={{ fontFamily: 'monospace' }}>{editOrg.id}</span>
              </div>
              <div className="form-group">
                <label className="form-label">Name</label>
                <input
                  type="text"
                  value={editForm.name}
                  onChange={(e) => setEditForm({ ...editForm, name: e.target.value })}
                  className="form-input"
                />
              </div>
              <div className="form-group">
                <label className="form-label">Plan</label>
                <select
                  value={editForm.plan}
                  onChange={(e) => setEditForm({ ...editForm, plan: e.target.value })}
                  className="detail-select"
                  style={{ width: '100%' }}
                >
                  <option value="free">Free</option>
                  <option value="pro">Pro</option>
                  <option value="enterprise">Enterprise</option>
                </select>
              </div>
              <div className="form-group">
                <label className="form-label">Storage Quota (MB)</label>
                <input
                  type="number"
                  min="0"
                  value={editForm.storage_quota_mb}
                  onChange={(e) => setEditForm({ ...editForm, storage_quota_mb: parseInt(e.target.value) || 0 })}
                  className="form-input"
                />
              </div>
            </div>
            <div className="modal-footer modal-footer-right">
              <button className="btn btn-secondary" onClick={() => setEditOrg(null)} disabled={editLoading}>
                Cancel
              </button>
              <button className="btn btn-primary" onClick={handleEdit} disabled={editLoading}>
                {editLoading ? 'Saving...' : 'Save'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Delete Organization Modal */}
      {deleteOrg && (
        <div className="modal-overlay" onClick={() => !deleteLoading && (setDeleteOrg(null), setDeleteInput(''))}>
          <div className="modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3 className="modal-title">Delete Organization</h3>
              <button onClick={() => { setDeleteOrg(null); setDeleteInput(''); }} className="btn-close" disabled={deleteLoading}>
                <CloseIcon sx={{ fontSize: '1.25rem' }} />
              </button>
            </div>
            <div className="modal-body">
              <p className="confirm-text">
                Delete organization <strong className="user-id-highlight">{deleteOrg.name}</strong>?
              </p>
              <p className="confirm-subtext">
                ID: <code>{deleteOrg.id}</code>. This action cannot be undone.
              </p>
              <p className="confirm-text" style={{ marginTop: '0.75rem', marginBottom: '0' }}>
                Type <strong className="user-id-highlight">DELETE</strong> to confirm.
              </p>
              <input
                type="text"
                value={deleteInput}
                onChange={(e) => setDeleteInput(e.target.value)}
                className="confirm-input"
                placeholder="Type DELETE"
              />
            </div>
            <div className="modal-footer modal-footer-right">
              <button
                className="btn btn-secondary"
                onClick={() => { setDeleteOrg(null); setDeleteInput(''); }}
                disabled={deleteLoading}
              >
                Cancel
              </button>
              <button
                className="btn btn-danger"
                onClick={handleDelete}
                disabled={deleteLoading || deleteInput !== 'DELETE'}
              >
                {deleteLoading ? 'Deleting...' : 'Delete Organization'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Organizations;
